// File: src/components/QuestionInput/QuestionInput.tsx

import { useCallback, useEffect, useState, useRef } from "react";
import {
    ArrowUp24Regular,
    Compose24Regular,
    Globe24Regular,
    BuildingRetail24Regular,
    Stop24Filled,
    Mic24Regular,
    Mic24Filled,
    Dismiss16Regular
} from "@fluentui/react-icons";
import styles from "./QuestionInput.module.css";
import { isN8NEnabled, clearN8NSession } from "../../api";
import { SiteId, SITE_ID_LABELS } from "../../api/models";
import { useSpeechInput, SpeechNotice, LastVoiceInput, SpeechBackendKind } from "./useSpeechInput";
import { useVoiceAutoSend } from "../../contexts/VoiceAutoSendContext";

// ENG-011: per-message input-mode analytics. "voice" carries whether the sent text still
// matches the recognized transcript verbatim (voiceEdited) and which backend produced it;
// "typed" is the default for anything not currently backed by a voice transcript.
// ENG-014: autoSubmitted marks a voice message sent by the auto-send grace timer, not a tap on send.
// ENG-034: speechBackend is a plain string (not just "browser" | "azure") because
// ChatWidget.submitText({inputMode:"voice"}) can carry a host-supplied backend id (e.g.
// "genie_native"); the gateway validates it against its own allow-list, see chat.py.
export type SendMeta = { inputMode: "voice"; voiceEdited: boolean; speechBackend: string; autoSubmitted?: boolean } | { inputMode: "typed" };

// ENG-014: how long the recognized text sits in the field before it is sent on its own — long
// enough to notice a misrecognition and stop it. ENG-069: the time now comes from the admin
// (speech.timeouts.autoSendDelayMs via Chat.tsx) and is shown as a draining bar, a countdown and an
// Edit button; these are the fallback when the config has not arrived or carries nothing usable,
// and the same bounds the gateway enforces (gateway/config.py SPEECH_AUTO_SEND_DELAY).
const DEFAULT_AUTO_SEND_DELAY_MS = 3000;
const MIN_AUTO_SEND_DELAY_MS = 1000;
const MAX_AUTO_SEND_DELAY_MS = 8000;

const resolveAutoSendDelayMs = (value: unknown): number =>
    typeof value === "number" && Number.isFinite(value)
        ? Math.min(MAX_AUTO_SEND_DELAY_MS, Math.max(MIN_AUTO_SEND_DELAY_MS, Math.round(value)))
        : DEFAULT_AUTO_SEND_DELAY_MS;

// ENG-034: ChatWidget.submitText() validation — empty/whitespace-only or longer than this
// rejects with Error("invalid_text") without touching the field.
const SUBMIT_TEXT_MAX_LENGTH = 4000;

interface Props {
    onSend: (question: string, meta?: SendMeta) => void;
    disabled: boolean;
    initQuestion?: string;
    placeholder?: string;
    clearOnSend?: boolean;
    onNewConversation?: () => void;
    // Response in progress props
    isResponseInProgress?: boolean;
    onStop?: () => void;
    // Widget prop
    isWidget?: boolean;
    // Question answers preview props
    questionAnswers?: string;
    onClearQuestionAnswers?: () => void;
    // Price source props
    priceSource: "web" | "store";
    onPriceSourceChange: (source: "web" | "store") => void;
    priceSourceLocked?: boolean;
    siteId: SiteId;
    onSiteIdChange: (siteId: SiteId) => void;
    siteIdLocked?: boolean;
    siteIdHasHistory?: boolean;
    exampleLanguage?: "el" | "en";
    // ENG-015: the admin kill switch for this client's channel. false hides the mic. Default true.
    speechEnabled?: boolean;
    // ENG-027: the field's text changed to something non-empty (typed or dictated) — the
    // user is composing, so a pending rating prompt must not pop up.
    onUserInput?: () => void;
    // ENG-054: the conversation thread on screen (utils/conversationThread.ts). "New chat" clears
    // only this thread's session, and the mic usage ping reports this thread's session.
    conversationThreadKey?: string;
    // ENG-069: how long the auto-send countdown lasts, in ms (admin: speech.timeouts.autoSendDelayMs).
    // Missing or not a number -> 3000; otherwise clamped to 1000-8000.
    autoSendDelayMs?: number;
}

const useUserInputSignal = (question: string, onUserInput: (() => void) | undefined) => {
    const callbackRef = useRef(onUserInput);
    callbackRef.current = onUserInput;
    useEffect(() => {
        if (question) {
            callbackRef.current?.();
        }
    }, [question]);
};

const useInitQuestion = (initQuestion: string | undefined, setQuestion: (value: string) => void) => {
    useEffect(() => {
        if (initQuestion) {
            setQuestion(initQuestion);
        }
    }, [initQuestion, setQuestion]);
};

const useAutoResizeTextarea = (textAreaRef: React.RefObject<HTMLTextAreaElement>, value: string) => {
    useEffect(() => {
        if (textAreaRef.current) {
            textAreaRef.current.style.height = "auto";
            textAreaRef.current.style.height = textAreaRef.current.scrollHeight + "px";
        }
    }, [value, textAreaRef]);
};

const useSendQuestionHandlers = (
    disabled: boolean,
    question: string,
    questionAnswers: string | undefined,
    onSend: (question: string, meta?: SendMeta) => void,
    clearOnSend: boolean | undefined,
    textAreaRef: React.RefObject<HTMLTextAreaElement>,
    setQuestion: (value: string) => void,
    lastVoiceInput: LastVoiceInput,
    clearLastInput: () => void,
    cancelAutoSend: () => void
) => {
    const sendQuestion = () => {
        if (disabled || (!question.trim() && !questionAnswers)) {
            return;
        }
        cancelAutoSend();
        // ENG-011: the field's text still carries a voice transcript — report which backend
        // produced it and whether the sent text still matches it verbatim. Otherwise typed.
        const meta: SendMeta = lastVoiceInput
            ? { inputMode: "voice", voiceEdited: question !== lastVoiceInput.transcript, speechBackend: lastVoiceInput.backend }
            : { inputMode: "typed" };
        onSend(question, meta);
        clearLastInput();

        if (clearOnSend) {
            setQuestion("");
            if (textAreaRef.current) {
                textAreaRef.current.style.height = "auto";
            }
        }
    };

    const onEnterPress = (ev: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (ev.key === "Enter" && !ev.shiftKey) {
            ev.preventDefault();
            sendQuestion();
        }
    };

    const onQuestionChange = (ev: React.ChangeEvent<HTMLTextAreaElement>) => {
        // ENG-014: any typing cancels a pending auto-send.
        cancelAutoSend();
        setQuestion(ev.target.value);
        // Emptying the field (backspace, select-all-delete) means whatever comes next is a
        // fresh message — drop the stale voice transcript so it isn't misreported as voice.
        if (ev.target.value === "") {
            clearLastInput();
        }
    };

    const sendQuestionDisabled = disabled || (!question.trim() && !questionAnswers);

    return { sendQuestion, onEnterPress, onQuestionChange, sendQuestionDisabled };
};

type AutoSendCountdownProps = {
    delayMs: number;
    exampleLanguage: "el" | "en";
    onEdit: () => void;
};

// ENG-069: shown while a voice / submitText() auto-send waits. The bar drains linearly over delayMs
// (CSS, hidden under prefers-reduced-motion); the number is whole seconds left, rounded up.
const AutoSendCountdown = ({ delayMs, exampleLanguage, onEdit }: AutoSendCountdownProps) => {
    const [secondsLeft, setSecondsLeft] = useState<number>(Math.ceil(delayMs / 1000));
    useEffect(() => {
        const startedAt = Date.now();
        const interval = setInterval(() => {
            setSecondsLeft(Math.max(1, Math.ceil((delayMs - (Date.now() - startedAt)) / 1000)));
        }, 200);
        return () => clearInterval(interval);
    }, [delayMs]);
    const label = exampleLanguage === "el" ? `Αποστολή σε ${secondsLeft}″` : `Sending in ${secondsLeft}s`;
    const editLabel = exampleLanguage === "el" ? "Διόρθωση" : "Edit";
    return (
        <div className={styles.autoSendRow}>
            <div className={styles.autoSendTrack} aria-hidden="true">
                <div className={styles.autoSendBar} style={{ animationDuration: `${delayMs}ms` }} />
            </div>
            <span className={styles.autoSendText} aria-hidden="true">
                {label}
            </span>
            <button className={styles.autoSendEditButton} onClick={onEdit} type="button">
                {editLabel}
            </button>
        </div>
    );
};

// ENG-071: relative height of each level bar (a voice-meter shape) and how low a bar goes at silence.
const LEVEL_BAR_WEIGHTS = [0.5, 0.8, 1, 0.8, 0.5];
const LEVEL_BAR_MIN_SCALE = 0.15;

// ENG-071: bars left of the mic while it listens, so the user sees that it hears something. With a
// level (Azure) their heights follow it, written straight to the DOM from requestAnimationFrame
// (never React state 60 times a second), rising fast and falling slower; with none (the browser
// backend, or Azure still starting) data-mode="pulse" hands them to a CSS pulse. Under
// prefers-reduced-motion nothing runs and the CSS keeps them still. Decorative: the mic's
// aria-pressed already says it is listening.
const VoiceLevelBars = ({ readLevel }: { readLevel: () => number | null }) => {
    const barsRef = useRef<HTMLSpanElement>(null);
    useEffect(() => {
        const container = barsRef.current;
        if (!container || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
            return;
        }
        const bars = Array.from(container.children) as HTMLElement[];
        let frame = 0;
        let shown = 0;
        let mode = "pulse";
        const tick = (now: number) => {
            const level = readLevel();
            const nextMode = level === null ? "pulse" : "level";
            if (nextMode !== mode) {
                mode = nextMode;
                container.dataset.mode = mode;
            }
            if (level !== null) {
                shown += (level - shown) * (level > shown ? 0.6 : 0.12);
                bars.forEach((bar, index) => {
                    const wobble = 0.85 + 0.15 * Math.sin(now / 90 + index * 1.7);
                    const scale = LEVEL_BAR_MIN_SCALE + (1 - LEVEL_BAR_MIN_SCALE) * Math.min(1, shown * LEVEL_BAR_WEIGHTS[index] * wobble);
                    bar.style.transform = `scaleY(${scale.toFixed(3)})`;
                });
            }
            frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame);
    }, [readLevel]);
    return (
        <span ref={barsRef} className={styles.voiceLevel} data-mode="pulse" aria-hidden="true">
            {LEVEL_BAR_WEIGHTS.map((_, index) => (
                <span key={index} className={styles.voiceLevelBar} />
            ))}
        </span>
    );
};

type QuestionAnswersPreviewProps = {
    questionAnswers: string;
    onClearQuestionAnswers?: () => void;
    exampleLanguage?: "el" | "en";
};

const QuestionAnswersPreview = ({ questionAnswers, onClearQuestionAnswers, exampleLanguage = "el" }: QuestionAnswersPreviewProps) => {
    const selectedAnswersLabel = exampleLanguage === "el" ? "Έτοιμες για αποστολή" : "Ready to send";
    const selectedAnswersHelpText =
        exampleLanguage === "el" ? "Αυτές οι απαντήσεις θα σταλούν με το επόμενο μήνυμά σου." : "These answers will be sent with your next message.";
    const clearSelectionsLabel = exampleLanguage === "el" ? "Καθαρισμός όλων των επιλογών" : "Clear all selections";
    const formatPreviewLine = (line: string) => {
        const separatorIndex = line.indexOf(":");
        if (separatorIndex === -1) {
            return { questionPart: "", answerPart: line };
        }

        return {
            questionPart: line.slice(0, separatorIndex).trim(),
            answerPart: line.slice(separatorIndex + 1).trim()
        };
    };
    const extractAnswerTokens = (answerPart: string): string[] => {
        const quotedMatches = Array.from(answerPart.matchAll(/'([^']+)'/g))
            .map(match => match[1].trim())
            .filter(Boolean);

        if (quotedMatches.length > 0) {
            return quotedMatches;
        }

        const normalized = answerPart.trim();
        return normalized ? [normalized] : [];
    };

    return (
        <div className={styles.questionAnswersPreview}>
            <div className={styles.previewHeader}>
                <div className={styles.previewTitleGroup}>
                    <span className={styles.previewTitle}>{selectedAnswersLabel}</span>
                    <span className={styles.previewSubtitle}>{selectedAnswersHelpText}</span>
                </div>
                <button
                    className={styles.previewClearBtn}
                    onClick={onClearQuestionAnswers}
                    title={clearSelectionsLabel}
                    aria-label={clearSelectionsLabel}
                    type="button"
                >
                    ✕
                </button>
            </div>
            <div className={styles.previewContent}>
                {questionAnswers.split("\n").map(line => {
                    const { questionPart, answerPart } = formatPreviewLine(line);
                    const answerTokens = extractAnswerTokens(answerPart);

                    return (
                        <div key={`${line}-${line.length}`} className={styles.previewLine}>
                            {questionPart ? (
                                <>
                                    <div className={styles.previewQuestionRow}>
                                        <span className={styles.previewBullet}>•</span>
                                        <span className={styles.previewQuestion}>{questionPart}:</span>
                                    </div>
                                    <div className={styles.previewAnswersRow}>
                                        {answerTokens.map((token, index) => (
                                            <span key={`${token}-${index}`} className={styles.previewAnswerChip}>
                                                {token}
                                            </span>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <div className={styles.previewAnswersRow}>
                                    {answerTokens.map((token, index) => (
                                        <span key={`${token}-${index}`} className={styles.previewAnswerChip}>
                                            {token}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

type PriceSourceToggleProps = {
    priceSourceLocked: boolean;
    priceSource: "web" | "store";
    priceSourceAriaLabel: string;
    nextPriceSource: "web" | "store";
    onPriceSourceChange: (source: "web" | "store") => void;
};

const PriceSourceToggle = ({ priceSourceLocked, priceSource, priceSourceAriaLabel, nextPriceSource, onPriceSourceChange }: PriceSourceToggleProps) => (
    <div className={styles.priceButtonWrapper}>
        <button
            className={`${styles.priceButton} ${priceSourceLocked ? styles.priceButtonLocked : ""}`}
            onClick={() => {
                if (priceSourceLocked) return;
                onPriceSourceChange(nextPriceSource);
            }}
            aria-label={priceSourceAriaLabel}
            type="button"
            disabled={priceSourceLocked}
        >
            {priceSource === "store" ? <BuildingRetail24Regular /> : <Globe24Regular />}
        </button>
        <span className={styles.tooltip}>{priceSourceAriaLabel}</span>
    </div>
);

type SiteIdToggleProps = {
    siteIdLocked: boolean;
    siteId: SiteId;
    nextSiteId: SiteId;
    siteIdAriaLabel: string;
    onSiteIdChange: (siteId: SiteId) => void;
    siteIdHasHistory?: boolean;
};

const SiteIdToggle = ({ siteIdLocked, siteId, nextSiteId, siteIdAriaLabel, onSiteIdChange, siteIdHasHistory }: SiteIdToggleProps) => {
    return (
        <div className={styles.siteIdButtonWrapper}>
            <button
                className={`${styles.siteIdButton} ${siteIdLocked ? styles.siteIdButtonLocked : ""}`}
                onClick={() => {
                    if (siteIdLocked) return;
                    onSiteIdChange(nextSiteId);
                }}
                aria-label={siteIdAriaLabel}
                type="button"
                disabled={siteIdLocked}
            >
                {SITE_ID_LABELS[siteId]}
            </button>
            <span className={styles.tooltip}>{siteIdAriaLabel}</span>
        </div>
    );
};

export const QuestionInput = ({
    onSend,
    disabled,
    placeholder,
    clearOnSend,
    initQuestion,
    onNewConversation,
    isResponseInProgress = false,
    onStop,
    isWidget,
    questionAnswers,
    onClearQuestionAnswers,
    priceSource,
    onPriceSourceChange,
    priceSourceLocked = false,
    siteId,
    onSiteIdChange,
    siteIdLocked = false,
    siteIdHasHistory = false,
    exampleLanguage = "el",
    speechEnabled = true,
    onUserInput,
    conversationThreadKey,
    autoSendDelayMs
}: Props) => {
    const [question, setQuestion] = useState<string>("");
    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    useUserInputSignal(question, onUserInput);

    useInitQuestion(initQuestion, setQuestion);
    useAutoResizeTextarea(textAreaRef, question);

    // ENG-014: per-user auto-send. A successful final voice result starts a grace timer; typing,
    // ×, a new mic tap, a manual send, a response starting, or the input being disabled cancels it.
    // ENG-069: the wait is the admin's autoSendDelayMs and is shown (bar, "Sending in n″", Edit);
    // Edit, a press inside the field or any key but Enter/Tab in it also stop it, keeping the text.
    const { enabled: voiceAutoSend, setAvailable: setVoiceAvailable } = useVoiceAutoSend();
    const autoSendDelay = resolveAutoSendDelayMs(autoSendDelayMs);
    const autoSendDelayRef = useRef(autoSendDelay);
    autoSendDelayRef.current = autoSendDelay;
    const [autoSendPending, setAutoSendPending] = useState<boolean>(false);
    // The countdown remounts per run (key) and keeps the delay its run started with, even if the
    // config changes meanwhile.
    const [autoSendRun, setAutoSendRun] = useState<{ id: number; delayMs: number }>({ id: 0, delayMs: autoSendDelay });
    const autoSendTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Assigned below, once onSend's collaborators (clearLastInput, …) exist; read when the timer fires.
    const autoSendFireRef = useRef<(text: string, backend: SpeechBackendKind) => void>(() => undefined);

    const cancelAutoSend = useCallback(() => {
        if (autoSendTimerRef.current) {
            clearTimeout(autoSendTimerRef.current);
            autoSendTimerRef.current = null;
        }
        setAutoSendPending(false);
    }, []);

    // ENG-069: the one place a countdown starts, for the mic (below) and for submitText() (ENG-034).
    const startAutoSend = useCallback(
        (fire: () => void) => {
            cancelAutoSend();
            const delayMs = autoSendDelayRef.current;
            setAutoSendRun(run => ({ id: run.id + 1, delayMs }));
            setAutoSendPending(true);
            autoSendTimerRef.current = setTimeout(() => {
                autoSendTimerRef.current = null;
                setAutoSendPending(false);
                fire();
            }, delayMs);
        },
        [cancelAutoSend]
    );

    // ENG-069: "Edit" / a press or a key in the field. Stops the countdown and keeps everything
    // (text, lastVoiceInput) — unlike × (clearQuestion), which also clears the text. Never called
    // for programmatic focus(): useSpeechInput and submitText() focus the field themselves when the
    // text arrives, so focus alone cannot be the signal.
    const editAutoSend = useCallback(
        (moveCaretToEnd: boolean) => {
            if (!autoSendTimerRef.current) {
                return;
            }
            cancelAutoSend();
            const textArea = textAreaRef.current;
            if (moveCaretToEnd && textArea) {
                textArea.focus();
                textArea.setSelectionRange(textArea.value.length, textArea.value.length);
            }
        },
        [cancelAutoSend]
    );

    const handleSpeechFinal = (text: string, backend: SpeechBackendKind) => {
        if (!voiceAutoSend || disabled || isResponseInProgress || !text.trim()) {
            return;
        }
        startAutoSend(() => autoSendFireRef.current(text, backend));
    };

    useEffect(() => {
        if (disabled || isResponseInProgress || !voiceAutoSend) {
            cancelAutoSend();
        }
    }, [disabled, isResponseInProgress, voiceAutoSend, cancelAutoSend]);

    useEffect(() => cancelAutoSend, [cancelAutoSend]);

    const {
        supported: speechInputSupported,
        listening: speechListening,
        notice: speechNotice,
        preparing: speechPreparing,
        lastInput: lastVoiceInput,
        clearLastInput,
        toggleListening: toggleSpeechListening,
        abortListening,
        recordExternalInput,
        readLevel: readSpeechLevel
    } = useSpeechInput({
        exampleLanguage,
        setQuestion,
        disabled: disabled || isResponseInProgress,
        textAreaRef,
        siteId,
        isWidget,
        onFinal: handleSpeechFinal,
        speechEnabled,
        onDisabledByAdmin: () => setVoiceAvailable(false),
        conversationThreadKey
    });

    const { sendQuestion, onEnterPress, onQuestionChange, sendQuestionDisabled } = useSendQuestionHandlers(
        disabled,
        question,
        questionAnswers,
        onSend,
        clearOnSend,
        textAreaRef,
        setQuestion,
        lastVoiceInput,
        clearLastInput,
        cancelAutoSend
    );

    autoSendFireRef.current = (text, backend) => {
        if (disabled || isResponseInProgress) {
            return;
        }
        onSend(text, { inputMode: "voice", voiceEdited: false, speechBackend: backend, autoSubmitted: true });
        clearLastInput();
        if (clearOnSend) {
            setQuestion("");
            if (textAreaRef.current) {
                textAreaRef.current.style.height = "auto";
            }
        }
    };

    // ENG-034: same grace-timer plumbing as the mic (autoSendTimerRef/cancelAutoSend — typing,
    // ×, a mic tap, a manual send, disabled/response-in-progress all cancel it the same way,
    // unchanged above), but its own fire callback: unlike the mic's (always voice), the SendMeta
    // here depends on the submitText() caller's options and may be "typed".
    const externalSendFireRef = useRef<(text: string, meta: SendMeta) => void>(() => undefined);
    externalSendFireRef.current = (text, meta) => {
        if (disabled || isResponseInProgress) {
            return;
        }
        onSend(text, meta);
        clearLastInput();
        if (clearOnSend) {
            setQuestion("");
            if (textAreaRef.current) {
                textAreaRef.current.style.height = "auto";
            }
        }
    };

    // ENG-034: window.ChatWidget.submitText(text, options) — text a host already produced (e.g.
    // Genie's native speech-to-text) replaces the field's content exactly like the mic (ENG-006).
    // Same event pattern as setHostContext(): widget.tsx dispatches "public-widget:submit-text"
    // and awaits "public-widget:submit-text-result" — this is the one place that can answer it,
    // since only the mounted field knows whether the chat is busy. When inputMode maps to
    // "voice", recordExternalInput() gives the field the same lastInput bookkeeping a mic result
    // gets, so a manual send (sendQuestion, unchanged above) reports the correct
    // voiceEdited/speechBackend on its own.
    //
    // options.send overrides the auto-send hierarchy for this one call (contract update, see
    // tickets/sent — the sent message differs from the ticket's original "no send option"):
    // true -> sends immediately via the same path as the mic's auto-send fire (autoSubmitted:
    // true for voice, no grace period), still subject to the busy check above; false -> fills
    // the field and guarantees no auto-send timer at all, even if the host's `autoSend` init
    // option or the employee's own preference would otherwise fire one; absent -> today's
    // hierarchy (host `autoSend` option > employee preference), via voiceAutoSend, with the
    // auto-send delay (ENG-069: autoSendDelayMs, 3 s by default). The Promise resolves once the text is in the field (send: false or
    // absent) or once the message has been dispatched (send: true).
    useEffect(() => {
        const buildVoiceMeta = (backend: string): SendMeta => ({
            inputMode: "voice",
            voiceEdited: false,
            speechBackend: backend,
            autoSubmitted: true
        });

        const handleSubmitText = (event: Event) => {
            const detail = (event as CustomEvent).detail as
                | {
                      text?: unknown;
                      options?: {
                          inputMode?: "voice" | "typed" | "android_voice" | "widget_voice";
                          speechBackend?: string;
                          send?: boolean;
                      };
                      requestId?: string;
                  }
                | undefined;
            if (!detail || !detail.requestId) {
                return;
            }
            const { requestId } = detail;
            const respond = (ok: boolean, error?: string) => {
                try {
                    window.dispatchEvent(new CustomEvent("public-widget:submit-text-result", { detail: { requestId, ok, error } }));
                } catch {}
            };

            // Order: widget_not_ready (widget.tsx, before this listener even exists) > busy >
            // invalid_options > invalid_text > accepted.
            if (disabled || isResponseInProgress) {
                respond(false, "busy");
                return;
            }

            const rawInputMode = detail.options?.inputMode;
            // "widget_voice" names the widget's OWN mic (which reports itself as voice/azure or
            // voice/browser through its own path, never through submitText) — accepting it here
            // as "voice" or silently downgrading it to "typed" would both mislabel analytics
            // that a host cannot see going wrong, so it is rejected instead.
            if (rawInputMode === "widget_voice") {
                respond(false, "invalid_options");
                return;
            }

            const text = typeof detail.text === "string" ? detail.text : "";
            if (!text.trim() || text.length > SUBMIT_TEXT_MAX_LENGTH) {
                respond(false, "invalid_text");
                return;
            }

            cancelAutoSend();
            // "android_voice" is the documented value; "voice" is kept for compatibility with
            // the same meaning. Anything else (including omitted) is "typed", the default.
            const inputMode: "voice" | "typed" = rawInputMode === "android_voice" || rawInputMode === "voice" ? "voice" : "typed";
            const backend = detail.options?.speechBackend || "genie_native";
            setQuestion(text);
            if (inputMode === "voice") {
                recordExternalInput(backend, text);
            } else {
                // A previous mic dictation may have left a stale lastInput behind; this message
                // is typed, not voice, and must not inherit it.
                clearLastInput();
            }
            textAreaRef.current?.focus();

            if (detail.options?.send === true) {
                const meta: SendMeta = inputMode === "voice" ? buildVoiceMeta(backend) : { inputMode: "typed" };
                externalSendFireRef.current(text, meta);
                respond(true);
                return;
            }

            respond(true);

            if (detail.options?.send === false) {
                // Explicit no-auto-send override: the field keeps the text, no timer, ever.
                return;
            }

            if (voiceAutoSend) {
                startAutoSend(() => externalSendFireRef.current(text, inputMode === "voice" ? buildVoiceMeta(backend) : { inputMode: "typed" }));
            }
        };

        window.addEventListener("public-widget:submit-text", handleSubmitText as EventListener);
        return () => window.removeEventListener("public-widget:submit-text", handleSubmitText as EventListener);
    }, [disabled, isResponseInProgress, voiceAutoSend, cancelAutoSend, startAutoSend, setQuestion, clearLastInput, recordExternalInput]);

    const clearQuestion = () => {
        if (speechListening) {
            abortListening();
        }
        setQuestion("");
        clearLastInput();
        cancelAutoSend();
        textAreaRef.current?.focus();
    };

    const onMicClick = () => {
        cancelAutoSend();
        toggleSpeechListening();
    };

    const handleNewConversation = () => {
        if (isN8NEnabled()) {
            clearN8NSession(siteId, undefined, conversationThreadKey);
            console.log("🧹 Cleared n8n session (new conversation)");
        }
        onNewConversation?.();
    };

    const manualInputPlaceholder = exampleLanguage === "en" ? "Add any additional clarification here..." : "Πρόσθεσε εδώ οποιαδήποτε επιπλέον διευκρίνιση...";
    const newConversationLabel = exampleLanguage === "el" ? "Νέα συνομιλία" : "New chat";
    const sendMessageLabel = exampleLanguage === "el" ? "Αποστολή μηνύματος" : "Send message";
    const stopResponseLabel = exampleLanguage === "el" ? "Διακοπή απάντησης" : "Stop response";
    const replyPlaceholder = exampleLanguage === "el" ? "Απάντησε στον Sales Assistant..." : "Reply to Sales Assistant...";
    const startListeningLabel = exampleLanguage === "el" ? "Πες το ερώτημά σου" : "Speak your question";
    const stopListeningLabel = exampleLanguage === "el" ? "Σταμάτα την ακρόαση" : "Stop listening";
    const speechButtonLabel = speechListening ? stopListeningLabel : startListeningLabel;
    const clearTextLabel = exampleLanguage === "el" ? "Καθαρισμός κειμένου" : "Clear text";
    const speechPermissionNoticeMessage = exampleLanguage === "el" ? "Δεν δόθηκε άδεια μικροφώνου" : "Microphone permission denied";
    const speechServiceNoticeMessage =
        exampleLanguage === "el"
            ? "Η αναγνώριση ομιλίας δεν είναι διαθέσιμη σε αυτόν τον browser (Safari: ενεργοποίησε την Υπαγόρευση)"
            : "Speech recognition is unavailable in this browser (Safari: enable Dictation)";
    const speechNoSpeechNoticeMessage =
        exampleLanguage === "el"
            ? "Δεν ακούστηκε ομιλία. Έλεγξε ποιο μικρόφωνο χρησιμοποιεί ο browser"
            : "No speech detected. Check which microphone the browser is using";
    const speechNoDeviceNoticeMessage = exampleLanguage === "el" ? "Δεν βρέθηκε μικρόφωνο" : "No microphone found";
    const speechNetworkNoticeMessage = exampleLanguage === "el" ? "Η αναγνώριση ομιλίας δεν έχει σύνδεση" : "Speech recognition has no connection";
    // ENG-019: GET /speech/token answered 429 (per-IP rate limit on the gateway).
    const speechRateLimitedNoticeMessage = exampleLanguage === "el" ? "Πολλές προσπάθειες, δοκίμασε σε λίγο" : "Too many attempts, try again shortly";
    const speechNoticeMessages: Record<SpeechNotice, string> = {
        permission: speechPermissionNoticeMessage,
        service: speechServiceNoticeMessage,
        "no-speech": speechNoSpeechNoticeMessage,
        "no-device": speechNoDeviceNoticeMessage,
        network: speechNetworkNoticeMessage,
        "rate-limited": speechRateLimitedNoticeMessage
    };
    const speechNoticeMessage = speechNotice ? speechNoticeMessages[speechNotice] : null;
    const speechPreparingMessage = exampleLanguage === "el" ? "Προετοιμασία μικροφώνου…" : "Preparing microphone…";
    // ENG-069: while the countdown shows (AutoSendCountdown) the red status line stays out of the way.
    const speechStatusMessage = autoSendPending ? null : speechPreparing ? speechPreparingMessage : speechNoticeMessage;
    const autoSendAnnouncement = autoSendPending
        ? exampleLanguage === "el"
            ? `Αποστολή σε ${Math.ceil(autoSendRun.delayMs / 1000)} δευτερόλεπτα. Πάτα Διόρθωση για αλλαγές.`
            : `Sending in ${Math.ceil(autoSendRun.delayMs / 1000)} seconds. Press Edit to change it.`
        : "";
    const onTextAreaKeyDown = (ev: React.KeyboardEvent<HTMLTextAreaElement>) => {
        // Enter sends right away (onEnterPress), Tab only moves on; any other key is the user editing.
        if (ev.key !== "Enter" && ev.key !== "Tab") {
            editAutoSend(false);
        }
        onEnterPress(ev);
    };
    const priceSourceLabel =
        priceSource === "store"
            ? exampleLanguage === "el"
                ? "Χρήση τιμών e-shop"
                : "Switch to web prices"
            : exampleLanguage === "el"
            ? "Χρήση τιμών καταστήματος"
            : "Switch to store prices";
    const priceSourceAriaLabel = priceSourceLocked
        ? priceSource === "store"
            ? exampleLanguage === "el"
                ? "Οι τιμές καταστήματος είναι κλειδωμένες"
                : "Store prices locked"
            : exampleLanguage === "el"
            ? "Οι τιμές e-shop είναι κλειδωμένες"
            : "Web prices locked"
        : priceSourceLabel;
    const nextPriceSource = priceSource === "store" ? "web" : "store";
    const nextSiteId = siteId === "PUBLIC_GR" ? "PUBLIC_CY" : "PUBLIC_GR";
    const siteIdAriaLabel = siteIdLocked
        ? exampleLanguage === "el"
            ? `Το ${SITE_ID_LABELS[siteId]} είναι κλειδωμένο`
            : `${SITE_ID_LABELS[siteId]} locked`
        : exampleLanguage === "el"
        ? `Αλλαγή σε ${SITE_ID_LABELS[nextSiteId]}`
        : `Switch to ${SITE_ID_LABELS[nextSiteId]}`;
    return (
        <div className={`${styles.questionInputContainer} ${isWidget ? styles.widget : ""} ${autoSendPending ? styles.autoSendActive : ""}`}>
            {/* Preview container for selected question answers */}
            {questionAnswers && (
                <QuestionAnswersPreview questionAnswers={questionAnswers} onClearQuestionAnswers={onClearQuestionAnswers} exampleLanguage={exampleLanguage} />
            )}

            {/* Text area row */}
            <div className={styles.textAreaRow}>
                <textarea
                    ref={textAreaRef}
                    className={styles.questionInputTextArea}
                    placeholder={questionAnswers ? manualInputPlaceholder : placeholder || replyPlaceholder}
                    value={question}
                    onChange={onQuestionChange}
                    onKeyDown={onTextAreaKeyDown}
                    onPointerDown={() => editAutoSend(false)}
                    rows={1}
                    disabled={disabled}
                />
                {question.trim().length > 0 && !disabled && (
                    <button className={styles.clearButton} onClick={clearQuestion} aria-label={clearTextLabel} type="button">
                        <Dismiss16Regular />
                    </button>
                )}
            </div>

            {/* ENG-069: the countdown (bar, "Sending in n″", Edit) while auto-send waits. Its own class, not
                .micPermissionMessage: waiting is not an error. Screen readers get the sentence below once,
                when the wait starts; the ticking number inside is aria-hidden. */}
            <div className={styles.srOnly} role="status" aria-live="polite">
                {autoSendAnnouncement}
            </div>
            {autoSendPending && (
                <AutoSendCountdown key={autoSendRun.id} delayMs={autoSendRun.delayMs} exampleLanguage={exampleLanguage} onEdit={() => editAutoSend(true)} />
            )}

            {/* Speech status: "Preparing microphone…" while resolving the backend/token, else the
                notice (permission, service, no-speech, no-device, network) - auto-hides after a few seconds */}
            {speechStatusMessage && (
                <div className={styles.micPermissionMessage} role="status">
                    {speechStatusMessage}
                </div>
            )}

            {/* Buttons row */}
            <div className={styles.buttonsRow}>
                <div className={styles.leftButtons}>
                    {/* New conversation button */}
                    {onNewConversation && (
                        <button className={styles.newConversationButton} onClick={handleNewConversation} aria-label={newConversationLabel} type="button">
                            <Compose24Regular />
                            <span className={styles.tooltip}>{newConversationLabel}</span>
                        </button>
                    )}

                    {/* Price source toggle button */}
                    <PriceSourceToggle
                        priceSourceLocked={priceSourceLocked}
                        priceSource={priceSource}
                        priceSourceAriaLabel={priceSourceAriaLabel}
                        nextPriceSource={nextPriceSource}
                        onPriceSourceChange={onPriceSourceChange}
                    />
                    <SiteIdToggle
                        siteIdLocked={siteIdLocked}
                        siteId={siteId}
                        nextSiteId={nextSiteId}
                        siteIdAriaLabel={siteIdAriaLabel}
                        onSiteIdChange={onSiteIdChange}
                        siteIdHasHistory={siteIdHasHistory}
                    />
                </div>

                <div className={styles.rightButtons}>
                    {/* ENG-071: level bars while the widget's own mic listens; no mic (speechEnabled: false), no bars */}
                    {speechInputSupported && speechListening && <VoiceLevelBars readLevel={readSpeechLevel} />}

                    {/* Speech-to-text button - only rendered when the browser supports it */}
                    {speechInputSupported && (
                        <button
                            className={`${styles.micButton} ${speechListening ? styles.micButtonListening : ""}`}
                            onClick={onMicClick}
                            aria-label={speechButtonLabel}
                            aria-pressed={speechListening}
                            disabled={disabled || isResponseInProgress}
                            type="button"
                        >
                            {speechListening ? <Mic24Filled /> : <Mic24Regular />}
                            <span className={styles.tooltip}>{speechButtonLabel}</span>
                        </button>
                    )}

                    {/* Stop button - shown when response is in progress */}
                    {isResponseInProgress && onStop ? (
                        <button className={styles.stopButton} onClick={onStop} aria-label={stopResponseLabel} type="button">
                            <Stop24Filled />
                            <span className={styles.tooltip}>{stopResponseLabel}</span>
                        </button>
                    ) : (
                        /* Send button - muted when empty */
                        <button
                            className={styles.sendButton}
                            disabled={sendQuestionDisabled}
                            onClick={sendQuestion}
                            aria-label={sendMessageLabel}
                            type="button"
                        >
                            <ArrowUp24Regular />
                            <span className={styles.tooltip}>{sendMessageLabel}</span>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};
